import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  Tabs,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SyncIcon from "@mui/icons-material/Sync";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BusinessCenterOutlinedIcon from "@mui/icons-material/BusinessCenterOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PixOutlinedIcon from "@mui/icons-material/PixOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import TimelapseOutlinedIcon from "@mui/icons-material/TimelapseOutlined";
import SentimentDissatisfiedOutlinedIcon from "@mui/icons-material/SentimentDissatisfiedOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { listPlatformDominios } from "../../../api/client";
import {
  getPlatformGlobalPassengerSales,
  getPlatformGlobalPassengerVendas,
  getPlatformGlobalPassengerParityCheck,
  postPlatformGlobalPassengerSyncBackfill,
  postPlatformPassengerFeesBackfill,
} from "../../../api/passagensApi";
import { FEATURE_PASSAGENS_REPASSES_ENABLED } from "../../../constants/featureFlags";
import PassengerRepasseRequestsPanel from "./PassengerRepasseRequestsPanel";
import PassengerMercadoPagoAuditPanel from "./PassengerMercadoPagoAuditPanel";
import PassengerPartnerReportAuditPanel from "./PassengerPartnerReportAuditPanel";
import PassengerTaxProfilesPanel from "./PassengerTaxProfilesPanel";
import PassengerPartnerGlobalFeesPanel from "./PassengerPartnerGlobalFeesPanel";
import PassengerKpiDrilldownDrawer from "./PassengerKpiDrilldownDrawer";
import PassengerFeesRecalcButton from "./PassengerFeesRecalcButton";
import PassengerSaleValueCell from "./PassengerSaleValueCell";
import PassengerPaymentCell, {
  breakdownEntries,
  mpPaymentTypeLabel,
  resolveMpPaymentFromRow,
} from "../../Passagens/passagemMpPaymentUi";
import { PASSENGER_KPI_METRICS } from "./passengerTaxConstants";
import PassengerWorkspaceFinancialExports from "./PassengerWorkspaceFinancialExports";
import { workspaceDetailPath } from "./platformConsoleRoutes";
import { BusyButton } from "./shared";
import {
  AnalyticsDataTable,
  AnalyticsKpiCard,
  AnalyticsKpiGrid,
  AnalyticsSection,
  useAnalyticsTableSort,
} from "./platformConsoleAnalyticsUi";
import { formatProductUsagePeriodBR } from "../../../utils/productUsageScreenLabel";
import { resolveTenantDatabaseName } from "../../../utils/resolveTenantDatabaseName";

const KPI_ACCENT = {
  tickets: "#1976d2",
  gmv: "#2e7d32",
  xpay: "#6a1b9a",
  cancelled: "#ed6c02",
  chargebacks: "#d32f2f",
  feePartner: "#0288d1",
  feeMp: "#5e35b1",
  feeXvision: "#00838f",
  feeSurcharge: "#f57c00",
  feesTotal: "#455a64",
  paymentPix: "#00a650",
  paymentCard: "#1565c0",
  paymentMpBalance: "#009ee3",
  mpPending: "#ed6c02",
  paymentIntentPending: "#ff9800",
  paymentIntentNotAccessed: "#78909c",
  paymentIntentRejected: "#c62828",
  paymentIntentExpired: "#ef6c00",
  funnelOpen: "#0288d1",
  funnelLost: "#6d4c41",
};

const STATUS_KEYS = [
  "emitida",
  "paga",
  "erro_emissao",
  "cancelada",
  "estornada",
  "chargeback",
];

const PARITY_STATUS_COLOR = {
  ok: "success",
  warning: "warning",
  error: "error",
  migration_pending: "default",
};

const passengerSalesFilterFieldSx = {
  width: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": { bgcolor: "background.paper" },
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    pr: 3,
  },
};

const passengerSalesFilterGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "minmax(0, 1fr)",
    sm: "repeat(2, minmax(0, 1fr))",
    md: "repeat(3, minmax(0, 1fr))",
    lg: "repeat(4, minmax(0, 1fr))",
  },
  gap: 2,
  alignItems: "end",
};

const TENANT_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.dominio },
  tickets: { type: "number", get: (r) => r.tickets },
  gmv: { type: "number", get: (r) => r.gmv },
  cancelled: { type: "number", get: (r) => r.cancelados },
};

const VENDAS_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.dominio },
  date: { type: "number", get: (r) => new Date(r.emitido_em || r.criado_em || 0).getTime() || 0 },
  passenger: { type: "string", get: (r) => r.nome_passageiro },
  route: { type: "string", get: (r) => [r.origem, r.destino].filter(Boolean).join(" → ") },
  value: { type: "number", get: (r) => r.valor_cobrado },
  status: { type: "string", get: (r) => r.status_venda },
  payment: {
    type: "string",
    get: (r) => resolveMpPaymentFromRow(r)?.label || r.forma_pagamento_nome || r.payment_rail,
  },
  partner: { type: "string", get: (r) => r.partner_key },
};

const PARITY_SORT_FIELDS = {
  workspace: { type: "string", get: (r) => r.dominio },
  legacy: { type: "number", get: (r) => r.legacy_bpe_messages },
  index: { type: "number", get: (r) => r.passagens_vendas_rows },
  delta: { type: "number", get: (r) => r.delta },
  status: { type: "string", get: (r) => r.status },
};

function localYmd(d) {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function defaultRange() {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29);
  return { from: localYmd(from), to: localYmd(to) };
}

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

function formatElapsedMs(ms) {
  if (ms == null || !Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function backfillStatusChipColor(status) {
  if (status === "ok") return "success";
  if (status === "error") return "error";
  if (status === "skipped") return "warning";
  if (status === "running") return "info";
  return "default";
}

function mapBackfillTenantResult(row, offsets, t) {
  const result = row?.result || {};
  const key = String(row.dominioId);
  const stillHasMore = offsets && Object.prototype.hasOwnProperty.call(offsets, key);
  const synced = Number(result.synced || 0);
  const errors = Number(result.errors || 0);
  const processed = Number(result.processed || 0);

  if (result.code === "table_missing") {
    return {
      status: "skipped",
      synced,
      errors,
      processed,
      autoMigrated: false,
      error: result.autoMigrateAttempted
        ? t("platformPage.passengerBackfillTableMissingAuto")
        : t("platformPage.passengerBackfillTableMissing"),
      note: null,
    };
  }
  if (!result.ok) {
    return {
      status: "error",
      synced,
      errors,
      processed,
      autoMigrated: false,
      error: result.message || t("platformPage.passengerBackfillError"),
      note: null,
    };
  }
  if (stillHasMore) {
    return {
      status: "running",
      synced,
      errors,
      processed,
      autoMigrated: result.autoMigrated === true,
      error: null,
      note: t("platformPage.passengerBackfillItemInProgress", { synced, processed }),
    };
  }

  const notes = [];
  if (result.autoMigrated) notes.push(t("platformPage.passengerBackfillItemAutoMigrated"));
  if (synced > 0) notes.push(t("platformPage.passengerBackfillItemSynced", { synced }));
  else if (processed === 0) notes.push(t("platformPage.passengerBackfillItemEmpty"));
  if (errors > 0) notes.push(t("platformPage.passengerBackfillItemErrors", { errors }));

  return {
    status: "ok",
    synced,
    errors,
    processed,
    autoMigrated: result.autoMigrated === true,
    error: null,
    note: notes.join(" · ") || null,
  };
}

function fmtCount(v) {
  return Number(v || 0).toLocaleString("pt-BR");
}

function fmtPct(part, whole, digits = 1) {
  const p = Number(part);
  const w = Number(whole);
  if (!Number.isFinite(p) || !Number.isFinite(w) || w <= 0) return null;
  return `${((p / w) * 100).toLocaleString("pt-BR", {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
  })}%`;
}

function kpiLines(...lines) {
  return lines.filter(Boolean);
}

export default function PassengerTicketSalesTabPanel({ onLoaded, onError, fixedDominioId = null, embedded = false }) {
  const { t } = useTranslation("app");
  const navigate = useNavigate();
  const isWorkspaceView = embedded || Boolean(fixedDominioId);
  const defaults = useMemo(() => defaultRange(), []);
  const [mainTab, setMainTab] = useState(0);
  const [from, setFrom] = useState(defaults.from);
  const [to, setTo] = useState(defaults.to);
  const [dominioId, setDominioId] = useState(fixedDominioId ? String(fixedDominioId) : "");
  const [status, setStatus] = useState("");
  const [partnerKey, setPartnerKey] = useState("");
  const [search, setSearch] = useState("");
  const [workspaceOptions, setWorkspaceOptions] = useState([]);

  const [summary, setSummary] = useState(null);
  const [vendas, setVendas] = useState(null);
  const [vendasPage, setVendasPage] = useState(0);
  const [vendasRowsPerPage, setVendasRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [vendasLoading, setVendasLoading] = useState(false);
  const [error, setError] = useState("");

  const [parityResult, setParityResult] = useState(null);
  const [parityLoading, setParityLoading] = useState(false);
  const [parityError, setParityError] = useState("");
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillProgress, setBackfillProgress] = useState(null);
  const [backfillResults, setBackfillResults] = useState(null);
  const [backfillTick, setBackfillTick] = useState(0);
  const [kpiDrilldown, setKpiDrilldown] = useState(null);
  const [mpPendingSyncing, setMpPendingSyncing] = useState(false);
  const [mpPendingSyncMsg, setMpPendingSyncMsg] = useState("");
  const [mpPendingSyncIsError, setMpPendingSyncIsError] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onLoadedRef.current = onLoaded;
    onErrorRef.current = onError;
  }, [onLoaded, onError]);

  useEffect(() => {
    if (fixedDominioId != null && String(fixedDominioId)) {
      setDominioId(String(fixedDominioId));
    }
  }, [fixedDominioId]);

  const backfillWorkspaceOptions = useMemo(() => {
    if (!fixedDominioId) return workspaceOptions;
    const match = workspaceOptions.find((w) => String(w.id) === String(fixedDominioId));
    if (match) return [match];
    return [{ id: fixedDominioId, label: `#${fixedDominioId}`, schema: null }];
  }, [fixedDominioId, workspaceOptions]);

  const filterParams = useMemo(() => {
    const p = { from, to };
    if (dominioId) p.dominioId = dominioId;
    if (status) p.status = status;
    if (partnerKey.trim()) p.partner_key = partnerKey.trim();
    if (search.trim()) p.search = search.trim();
    return p;
  }, [from, to, dominioId, status, partnerKey, search]);

  const {
    sortBy: vendasSortBy,
    sortOrder: vendasSortOrder,
    handleSort: handleVendasSort,
  } = useAnalyticsTableSort("passenger-vendas", VENDAS_SORT_FIELDS, { sortBy: "date", sortOrder: "desc" });

  const handleVendasSortChange = useCallback(
    (columnId) => {
      handleVendasSort(columnId);
      setVendasPage(0);
    },
    [handleVendasSort]
  );

  const summaryParams = useMemo(() => {
    const p = { from, to };
    if (dominioId) p.dominioId = dominioId;
    return p;
  }, [from, to, dominioId]);

  const selectedWorkspaceLabel = useMemo(() => {
    if (!dominioId) return "";
    const w = workspaceOptions.find((row) => String(row.id) === String(dominioId));
    return w?.label || `#${dominioId}`;
  }, [dominioId, workspaceOptions]);

  const periodLabel = useMemo(
    () => formatProductUsagePeriodBR(summary?.period?.from || from, summary?.period?.to || to),
    [summary, from, to]
  );

  const statusLabel = useCallback(
    (key) =>
      t(`platformPage.passengerStatus_${key}`, {
        defaultValue: String(key || "")
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase()),
      }),
    [t]
  );

  useEffect(() => {
    listPlatformDominios({ tenantLegacyStats: "0" })
      .then((res) => {
        const items = (res?.items || [])
          .filter((d) => String(d.workspace_segment || "").toLowerCase() === "passenger_transport")
          .map((d) => ({
            id: d.id,
            label: d.dominio || d.name || `#${d.id}`,
            schema: resolveTenantDatabaseName(d.dominio, d.bd),
          }));
        setWorkspaceOptions(items);
      })
      .catch(() => setWorkspaceOptions([]));
  }, []);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getPlatformGlobalPassengerSales(summaryParams);
      setSummary(data && typeof data === "object" ? data : { ok: true, totals: {}, tenants: [] });
    } catch (err) {
      setSummary(null);
      setError(err?.response?.data?.message || err?.message || t("platformPage.passengerSalesLoadError"));
      onErrorRef.current?.(err);
    } finally {
      setLoading(false);
      onLoadedRef.current?.();
    }
  }, [summaryParams, t]);

  const loadVendas = useCallback(async () => {
    setVendasLoading(true);
    try {
      const data = await getPlatformGlobalPassengerVendas({
        ...filterParams,
        limit: vendasRowsPerPage,
        offset: vendasPage * vendasRowsPerPage,
        sortBy: vendasSortBy,
        sortOrder: vendasSortOrder,
      });
      setVendas(data);
    } catch (err) {
      setVendas(null);
      setError(err?.response?.data?.message || err?.message || t("platformPage.passengerSalesVendasLoadError"));
    } finally {
      setVendasLoading(false);
    }
  }, [filterParams, vendasPage, vendasRowsPerPage, vendasSortBy, vendasSortOrder, t]);

  const filterParamsKey = useMemo(() => JSON.stringify(filterParams), [filterParams]);
  const prevFilterParamsKeyRef = useRef(filterParamsKey);

  useEffect(() => {
    if (prevFilterParamsKeyRef.current !== filterParamsKey) {
      prevFilterParamsKeyRef.current = filterParamsKey;
      setVendasPage(0);
    }
  }, [filterParamsKey]);

  const load = useCallback(async () => {
    await loadSummary();
    if (mainTab === 1) await loadVendas();
  }, [loadSummary, loadVendas, mainTab]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (mainTab === 1) loadVendas();
  }, [mainTab, loadVendas]);

  const resetToPlatform = () => setDominioId("");

  const runParityCheck = async () => {
    setParityLoading(true);
    setParityError("");
    setParityResult(null);
    try {
      const data = await getPlatformGlobalPassengerParityCheck(summaryParams);
      setParityResult(data);
    } catch (err) {
      setParityError(err?.response?.data?.message || err?.message || t("platformPage.passengerParityError"));
    } finally {
      setParityLoading(false);
    }
  };

  const runBackfillAll = async () => {
    if (backfillRunning) return;
    const targets = backfillWorkspaceOptions;
    if (!targets.length) {
      setBackfillResults({ error: t("platformPage.passengerBackfillNoWorkspaces") });
      return;
    }

    const startedAt = Date.now();
    setBackfillRunning(true);
    setBackfillResults(null);

    const initialItems = targets.map((w) => ({
      dominioId: w.id,
      dominio: w.label,
      schema: w.schema || null,
      status: "pending",
      synced: 0,
      errors: 0,
      processed: 0,
      note: null,
      error: null,
      startedAt: null,
      finishedAt: null,
      durationMs: null,
      autoMigrated: false,
    }));

    let items = [...initialItems];
    setBackfillProgress({
      items,
      batch: 0,
      totalSynced: 0,
      totalErrors: 0,
      tenantsTotal: initialItems.length,
      tenantOffset: 0,
      startedAt,
    });

    let offsets = {};
    let tenantOffset = 0;
    let batch = 0;
    let totalSynced = 0;
    let totalErrors = 0;
    let totalAutoMigrated = 0;
    let prevStateKey = JSON.stringify({ offsets, tenantOffset });
    let terminalError = null;
    let transientRetries = 0;
    const TENANTS_PER_UI_BATCH = 1;
    const MAX_BACKFILL_BATCHES = 200;
    const MAX_TRANSIENT_RETRIES = 6;

    const isTransientBackfillError = (err) => {
      const status = Number(err?.response?.status || 0);
      const code = String(err?.code || "");
      const msg = String(err?.response?.data?.message || err?.message || "").toLowerCase();
      return (
        code === "ECONNABORTED" ||
        status === 502 ||
        status === 503 ||
        status === 504 ||
        status === 408 ||
        (status === 500 && /timed?\s*out|internal server error|endpoint request timed out/i.test(msg)) ||
        /timed?\s*out|network error/i.test(msg)
      );
    };

    const syncProgress = (extra = {}) => {
      setBackfillProgress((prev) => ({
        ...prev,
        items: [...items],
        batch,
        totalSynced,
        totalErrors,
        tenantOffset,
        ...extra,
      }));
    };

    const revertUnfinishedRunningToPending = () => {
      items = items.map((it) => {
        if (it.status !== "running") return it;
        return {
          ...it,
          status: "pending",
          startedAt: null,
          finishedAt: null,
          durationMs: null,
          note: null,
          error: null,
        };
      });
      syncProgress();
    };

    const markRunningBeforeBatch = () => {
      const now = Date.now();
      const runningIds = new Set(Object.keys(offsets || {}));
      const pendingItems = items.filter((it) => it.status === "pending");
      const slotsLeft = Math.max(0, TENANTS_PER_UI_BATCH - runningIds.size);
      pendingItems.slice(0, slotsLeft).forEach((it) => runningIds.add(String(it.dominioId)));

      runningIds.forEach((id) => {
        const idx = items.findIndex((it) => String(it.dominioId) === id);
        if (idx < 0) return;
        if (items[idx].status === "pending" || items[idx].status === "running") {
          items[idx] = {
            ...items[idx],
            status: "running",
            startedAt: items[idx].startedAt || now,
          };
        }
      });
      syncProgress();
    };

    const applyBatchResults = (data) => {
      const now = Date.now();
      for (const row of data?.tenants || []) {
        const mapped = mapBackfillTenantResult(row, data?.offsets, t);
        const idx = items.findIndex((it) => String(it.dominioId) === String(row.dominioId));
        if (idx < 0) continue;
        const existing = items[idx];
        const itemStartedAt = existing.startedAt || now;
        const finished = ["ok", "skipped", "error"].includes(mapped.status);
        items[idx] = {
          ...existing,
          dominio: row.dominio || existing.dominio,
          schema: row.schema || existing.schema,
          ...mapped,
          startedAt: itemStartedAt,
          finishedAt: finished ? now : null,
          durationMs: finished ? now - itemStartedAt : existing.durationMs,
        };
      }
      tenantOffset = Number(data?.nextTenantOffset ?? data?.tenantOffset ?? tenantOffset);
      syncProgress({
        tenantsTotal: data?.tenantsTotal ?? initialItems.length,
        tenantOffset,
      });
    };

    const finalizeResults = (partial = false) => {
      const now = Date.now();
      const incomplete = items.filter((it) => it.status === "pending" || it.status === "running");
      if (incomplete.length > 0) {
        for (const it of incomplete) {
          const idx = items.findIndex((row) => row.dominioId === it.dominioId);
          if (idx < 0) continue;
          items[idx] = {
            ...items[idx],
            status: "error",
            error: t("platformPage.passengerBackfillItemNotProcessed"),
            finishedAt: now,
            durationMs: items[idx].startedAt ? now - items[idx].startedAt : null,
          };
        }
        if (!terminalError) {
          terminalError = t("platformPage.passengerBackfillIncomplete", { count: incomplete.length });
        }
        partial = true;
      }

      const ok = items.filter((i) => i.status === "ok").length;
      const skipped = items.filter((i) => i.status === "skipped").length;
      const failed = items.filter((i) => i.status === "error").length;
      setBackfillResults({
        error: terminalError || null,
        partial: partial || Boolean(terminalError),
        total: items.length,
        ok,
        skipped,
        failed,
        synced: totalSynced,
        errors: totalErrors,
        autoMigrated: totalAutoMigrated,
        durationMs: Date.now() - startedAt,
        items: [...items],
      });
      setBackfillProgress(null);
    };

    try {
      for (;;) {
        batch += 1;
        markRunningBeforeBatch();

        let data;
        try {
          data = await postPlatformGlobalPassengerSyncBackfill(summaryParams, { offsets, tenantOffset });
          transientRetries = 0;
        } catch (err) {
          if (isTransientBackfillError(err) && transientRetries < MAX_TRANSIENT_RETRIES) {
            transientRetries += 1;
            batch -= 1;
            revertUnfinishedRunningToPending();
            await new Promise((r) => setTimeout(r, Math.min(2000 * transientRetries, 8000)));
            continue;
          }
          throw err;
        }

        totalSynced += Number(data?.summary?.synced || 0);
        totalErrors += Number(data?.summary?.errors || 0);
        totalAutoMigrated += Number(data?.summary?.autoMigrated || 0);
        applyBatchResults(data);

        if (data?.stopReason === "table_missing_all") {
          terminalError =
            Number(data?.summary?.autoMigrated || 0) > 0
              ? t("platformPage.passengerBackfillTableMissingPartial", {
                  skipped: data?.summary?.skipped ?? 0,
                })
              : t("platformPage.passengerBackfillTableMissingAuto");
          break;
        }
        if (data?.stopReason === "all_failed") {
          terminalError = t("platformPage.passengerBackfillAllFailed");
          break;
        }
        if (!data?.hasMore) break;

        const nextOffsets = data.offsets && typeof data.offsets === "object" ? data.offsets : {};
        const nextTenantOffset = Number(data?.nextTenantOffset ?? data?.tenantOffset ?? tenantOffset);
        const nextStateKey = JSON.stringify({ offsets: nextOffsets, tenantOffset: nextTenantOffset });
        if (nextStateKey === prevStateKey) {
          terminalError = t("platformPage.passengerBackfillStalled");
          break;
        }
        prevStateKey = nextStateKey;
        offsets = nextOffsets;
        tenantOffset = nextTenantOffset;

        if (batch >= MAX_BACKFILL_BATCHES) {
          terminalError = t("platformPage.passengerBackfillMaxBatches");
          break;
        }
      }

      finalizeResults(Boolean(terminalError));
      await loadSummary();
    } catch (err) {
      terminalError = err?.response?.data?.message || err?.message || t("platformPage.passengerBackfillError");
      finalizeResults(true);
    } finally {
      setBackfillRunning(false);
    }
  };

  const backfillStatusLabel = useCallback(
    (status) => {
      if (status === "pending") return t("platformPage.migrateStatusPending");
      if (status === "running") return t("platformPage.migrateStatusRunning");
      if (status === "ok") return t("platformPage.migrateStatusOk");
      if (status === "skipped") return t("platformPage.passengerBackfillStatusSkipped");
      if (status === "error") return t("platformPage.migrateStatusError");
      return String(status || "—");
    },
    [t]
  );

  const backfillElapsedForItem = useCallback(
    (item) => {
      if (item?.durationMs != null) return formatElapsedMs(item.durationMs);
      if (item?.status === "running" && item.startedAt) {
        void backfillTick;
        return formatElapsedMs(Date.now() - item.startedAt);
      }
      return "—";
    },
    [backfillTick]
  );

  const backfillProgressSummary = useMemo(() => {
    const progressItems = backfillProgress?.items;
    if (!progressItems?.length) return null;
    const done = progressItems.filter((it) => ["ok", "skipped", "error"].includes(it.status)).length;
    const ok = progressItems.filter((it) => it.status === "ok").length;
    const skipped = progressItems.filter((it) => it.status === "skipped").length;
    const failed = progressItems.filter((it) => it.status === "error").length;
    const running = progressItems.filter((it) => it.status === "running").length;
    return { done, ok, skipped, failed, running, total: progressItems.length };
  }, [backfillProgress]);

  useEffect(() => {
    if (!backfillRunning) return undefined;
    const id = window.setInterval(() => setBackfillTick((n) => n + 1), 500);
    return () => window.clearInterval(id);
  }, [backfillRunning]);

  const renderBackfillItemIcon = (status) => {
    if (status === "ok") return <CheckCircleIcon fontSize="small" color="success" />;
    if (status === "error") return <ErrorIcon fontSize="small" color="error" />;
    if (status === "skipped") return <SkipNextIcon fontSize="small" color="warning" />;
    if (status === "running") return <CircularProgress size={18} />;
    return <HourglassEmptyIcon fontSize="small" color="disabled" />;
  };

  const renderBackfillItemsList = (rows, keyPrefix = "bf") => (
    <Paper variant="outlined" sx={{ maxHeight: 360, overflow: "auto" }}>
      <List dense disablePadding>
        {(rows || []).map((item) => (
          <ListItem
            key={`${keyPrefix}-${item.dominioId}`}
            alignItems="flex-start"
            sx={{
              py: 0.75,
              borderBottom: 1,
              borderColor: "divider",
              "&:last-child": { borderBottom: 0 },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, mt: 0.25 }}>{renderBackfillItemIcon(item.status)}</ListItemIcon>
            <ListItemText
              primary={
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                      {item.dominio}
                    </Typography>
                    {item.schema ? (
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block">
                        {item.schema}
                      </Typography>
                    ) : null}
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                    <Chip
                      size="small"
                      label={backfillStatusLabel(item.status)}
                      color={backfillStatusChipColor(item.status)}
                      variant={item.status === "pending" ? "outlined" : "filled"}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48, textAlign: "right" }}>
                      {backfillElapsedForItem(item)}
                    </Typography>
                  </Stack>
                </Stack>
              }
              secondary={item.error || item.note || null}
              secondaryTypographyProps={{
                variant: "caption",
                color: item.error ? "error" : "text.secondary",
              }}
            />
          </ListItem>
        ))}
      </List>
    </Paper>
  );

  const totals = summary?.totals || {};
  const paymentIntent = totals.payment_intent || {};
  const paymentIntentByScenario = paymentIntent.by_scenario || {};

  const feeTotals = useMemo(() => {
    const taxaParceiro = Number(totals.taxa_parceiro || 0);
    const taxaMp = Number(totals.taxa_mp || 0);
    const taxaXvision = Number(totals.taxa_xvision || 0);
    const sobretaxa = Number(totals.sobretaxa || 0);
    return {
      taxa_parceiro: taxaParceiro,
      taxa_mp: taxaMp,
      taxa_xvision: taxaXvision,
      sobretaxa,
      total: taxaParceiro + taxaMp + taxaXvision + sobretaxa,
    };
  }, [totals]);

  const kpiMeta = useMemo(() => {
    const tickets = Number(totals.tickets || 0);
    const gmv = Number(totals.gmv || 0);
    const xpayTickets = Number(totals.xpay_tickets || 0);
    const xpayGmv = Number(totals.xpay_gmv || 0);
    const cancelados = Number(totals.cancelados || 0);
    const canceladosGmv = Number(totals.cancelados_gmv || 0);
    const chargebacks = Number(totals.chargebacks || 0);
    const chargebacksGmv = Number(totals.chargebacks_gmv || 0);
    const avgTicket = tickets > 0 ? gmv / tickets : null;
    const avgXpayTicket = xpayTickets > 0 ? xpayGmv / xpayTickets : null;
    const pct = (part, whole, labelKey) => {
      const v = fmtPct(part, whole);
      return v ? t(labelKey, { pct: v }) : null;
    };

    return {
      tickets: kpiLines(
        tickets > 0 && avgTicket != null
          ? t("platformPage.passengerKpiAvgTicket", { value: fmtMoney(avgTicket) })
          : null,
        pct(xpayTickets, tickets, "platformPage.passengerKpiPctXpayOfTickets")
      ),
      gmv: kpiLines(
        tickets > 0 ? t("platformPage.passengerKpiTicketCount", { count: fmtCount(tickets) }) : null,
        tickets > 0 && avgTicket != null
          ? t("platformPage.passengerKpiAvgTicket", { value: fmtMoney(avgTicket) })
          : null
      ),
      xpayTickets: kpiLines(
        pct(xpayTickets, tickets, "platformPage.passengerKpiPctOfTickets"),
        xpayGmv > 0 ? t("platformPage.passengerKpiAmount", { amount: fmtMoney(xpayGmv) }) : null
      ),
      xpayGmv: kpiLines(
        pct(xpayGmv, gmv, "platformPage.passengerKpiPctOfGmv"),
        xpayTickets > 0 && avgXpayTicket != null
          ? t("platformPage.passengerKpiAvgTicket", { value: fmtMoney(avgXpayTicket) })
          : null,
        xpayTickets > 0
          ? t("platformPage.passengerKpiTicketCount", { count: fmtCount(xpayTickets) })
          : null
      ),
      cancelados: kpiLines(
        cancelados > 0 ? t("platformPage.passengerKpiAmount", { amount: fmtMoney(canceladosGmv) }) : null,
        pct(cancelados, tickets, "platformPage.passengerKpiPctOfTickets"),
        pct(canceladosGmv, gmv, "platformPage.passengerKpiPctOfGmv")
      ),
      chargebacks: kpiLines(
        chargebacks > 0 ? t("platformPage.passengerKpiAmount", { amount: fmtMoney(chargebacksGmv) }) : null,
        pct(chargebacks, tickets, "platformPage.passengerKpiPctOfTickets"),
        pct(chargebacksGmv, gmv, "platformPage.passengerKpiPctOfGmv"),
        t("platformPage.passengerChargebackKpiPeriodHint")
      ),
      emAndamento: kpiLines(
        Number(totals.em_andamento_gmv || 0) > 0
          ? t("platformPage.passengerKpiAmount", { amount: fmtMoney(totals.em_andamento_gmv) })
          : null,
        t("platformPage.passengerFunnelIsolatedHint")
      ),
      vendasPerdidas: kpiLines(
        Number(totals.vendas_perdidas_gmv || 0) > 0
          ? t("platformPage.passengerKpiAmount", { amount: fmtMoney(totals.vendas_perdidas_gmv) })
          : null,
        totals.funnel_stale_hours
          ? t("platformPage.passengerFunnelLostStaleHint", { hours: totals.funnel_stale_hours })
          : t("platformPage.passengerFunnelIsolatedHint")
      ),
      taxaParceiro: kpiLines(
        pct(feeTotals.taxa_parceiro, gmv, "platformPage.passengerKpiPctOfGmv"),
        xpayGmv > 0 ? pct(feeTotals.taxa_parceiro, xpayGmv, "platformPage.passengerKpiPctOfXpayGmv") : null
      ),
      taxaMp: kpiLines(
        pct(feeTotals.taxa_mp, xpayGmv, "platformPage.passengerKpiPctOfXpayGmv"),
        pct(feeTotals.taxa_mp, gmv, "platformPage.passengerKpiPctOfGmv")
      ),
      taxaXvision: kpiLines(
        pct(feeTotals.taxa_xvision, gmv, "platformPage.passengerKpiPctOfGmv"),
        xpayGmv > 0 ? pct(feeTotals.taxa_xvision, xpayGmv, "platformPage.passengerKpiPctOfXpayGmv") : null
      ),
      sobretaxa: kpiLines(pct(feeTotals.sobretaxa, gmv, "platformPage.passengerKpiPctOfGmv")),
      feesTotal: kpiLines(
        pct(feeTotals.total, gmv, "platformPage.passengerKpiPctOfGmv"),
        xpayGmv > 0 ? pct(feeTotals.total, xpayGmv, "platformPage.passengerKpiPctOfXpayGmv") : null
      ),
      paymentIntentPending: kpiLines(
        Number(paymentIntent.undiagnosed || 0) > 0
          ? t("platformPage.passengerPaymentIntentUndiagnosed", {
              count: fmtCount(paymentIntent.undiagnosed),
            })
          : null,
        t("platformPage.passengerPaymentIntentKpiHint")
      ),
      paymentIntentNotAccessed: kpiLines(
        pct(Number(paymentIntentByScenario.link_not_accessed || 0), Number(paymentIntent.total_pending || 0), "platformPage.passengerKpiPctOfPendingLinks")
      ),
      paymentIntentRejected: kpiLines(
        pct(Number(paymentIntentByScenario.payment_rejected || 0), Number(paymentIntent.total_pending || 0), "platformPage.passengerKpiPctOfPendingLinks")
      ),
      paymentIntentExpired: kpiLines(
        pct(Number(paymentIntentByScenario.link_expired || 0), Number(paymentIntent.total_pending || 0), "platformPage.passengerKpiPctOfPendingLinks")
      ),
      paymentIntentInReview: kpiLines(
        pct(Number(paymentIntentByScenario.payment_pending || 0), Number(paymentIntent.total_pending || 0), "platformPage.passengerKpiPctOfPendingLinks")
      ),
    };
  }, [totals, feeTotals, paymentIntent, paymentIntentByScenario, t]);

  const openKpiDrilldown = useCallback((key, title) => {
    setKpiDrilldown({ key: `kpi_${key}`, kpi: key, title });
  }, []);

  const openPaymentTypeDrilldown = useCallback((item) => {
    setKpiDrilldown({
      key: `payment_type_${item.key}`,
      title: item.label,
      mpPaymentType: item.key,
    });
  }, []);

  const openInstallmentsDrilldown = useCallback(
    (item) => {
      setKpiDrilldown({
        key: `installments_${item.key}`,
        title: t("platformPage.passengerSalesDrilldownInstallments", {
          label: item.label,
          defaultValue: `Parcelamento — ${item.label}`,
        }),
        mpInstallments: Number(item.key),
      });
    },
    [t]
  );

  const runMpPendingSync = useCallback(async () => {
    if (mpPendingSyncing) return;
    setMpPendingSyncing(true);
    setMpPendingSyncMsg("");
    setMpPendingSyncIsError(false);
    try {
      let totalUpdated = 0;
      let totalErrors = 0;
      let totalRemaining = 0;
      let mpTokenMissing = false;
      let tenantOffset = 0;
      let hasMore = true;
      let loops = 0;
      while (hasMore && loops < 40) {
        const res = await postPlatformPassengerFeesBackfill({
          from,
          to,
          dominioId: dominioId || undefined,
          syncMpPaymentDetailsOnly: true,
          forceMpBackfill: true,
          tenantOffset,
          limit: 150,
        });
        if (res?.mpTokenMissing) mpTokenMissing = true;
        totalUpdated += Number(res?.mpDetailsUpdated ?? res?.updated ?? 0);
        totalErrors += Number(res?.errors ?? 0);
        if (res?.mpPendingRemaining != null) {
          totalRemaining = dominioId
            ? Number(res.mpPendingRemaining)
            : totalRemaining + Number(res.mpPendingRemaining);
        }
        hasMore = Boolean(res?.hasMore);
        tenantOffset = Number(res?.tenantOffset ?? tenantOffset);
        loops += 1;
        if (!res?.ok) break;
        if (!hasMore) break;
      }
      setMpPendingSyncMsg(
        mpTokenMissing && totalUpdated === 0
          ? t("platformPage.passengerMpPendingSyncTokenMissing")
          : totalRemaining > 0
            ? t("platformPage.passengerMpPendingSyncPartial", {
                updated: totalUpdated,
                remaining: totalRemaining,
                errors: totalErrors,
                defaultValue: `${totalUpdated} venda(s) atualizada(s); ${totalRemaining} ainda aguardando sync do MP.`,
              })
            : t("platformPage.passengerMpPendingSyncDone", {
                updated: totalUpdated,
                errors: totalErrors,
                defaultValue: `Sincronização concluída: ${totalUpdated} venda(s) atualizada(s).`,
              })
      );
      setMpPendingSyncIsError(mpTokenMissing || (totalRemaining > 0 && totalUpdated === 0));
      await loadSummary();
      if (mainTab === 1) await loadVendas();
    } catch (err) {
      setMpPendingSyncMsg(
        err?.response?.data?.message || err?.message || t("platformPage.passengerMpPendingSyncError")
      );
      setMpPendingSyncIsError(true);
    } finally {
      setMpPendingSyncing(false);
    }
  }, [mpPendingSyncing, from, to, dominioId, loadSummary, loadVendas, mainTab, t]);

  const {
    sortBy: tenantSortBy,
    sortOrder: tenantSortOrder,
    handleSort: handleTenantSort,
    sortRows: sortTenantRows,
  } = useAnalyticsTableSort("passenger-tenants", TENANT_SORT_FIELDS, { sortBy: "workspace", sortOrder: "asc" });

  const {
    sortBy: paritySortBy,
    sortOrder: paritySortOrder,
    handleSort: handleParitySort,
    sortRows: sortParityRows,
  } = useAnalyticsTableSort("passenger-parity", PARITY_SORT_FIELDS, { sortBy: "workspace", sortOrder: "asc" });

  const tenantRows = useMemo(
    () =>
      sortTenantRows(
        (summary?.tenants || []).map((row) => ({
          ...row,
          _rowKey: String(row.dominioId),
        }))
      ),
    [summary, sortTenantRows]
  );

  const vendasRows = useMemo(
    () =>
      (vendas?.rows || []).map((row) => ({
        ...row,
        _rowKey: `${row.dominioId}-${row.id}`,
      })),
    [vendas]
  );

  const vendasTotals = vendas?.totals || null;

  const vendasStatusBreakdown = useMemo(() => {
    const byStatus = vendasTotals?.byStatus || {};
    const known = STATUS_KEYS.filter((key) => Number(byStatus[key]?.count || 0) > 0);
    const extra = Object.keys(byStatus)
      .filter((key) => !STATUS_KEYS.includes(key) && Number(byStatus[key]?.count || 0) > 0)
      .sort();
    return [...known, ...extra].map((key) => ({
      key,
      count: Number(byStatus[key]?.count || 0),
      gmv: Number(byStatus[key]?.gmv || 0),
    }));
  }, [vendasTotals]);

  const vendasPaymentBreakdown = useMemo(
    () =>
      breakdownEntries(vendasTotals?.byMpPaymentType, (key) => mpPaymentTypeLabel(key, undefined, t)),
    [vendasTotals, t]
  );

  const vendasInstallmentsBreakdown = useMemo(() => {
    const byInst = vendasTotals?.byInstallments || {};
    return Object.entries(byInst)
      .filter(([key, row]) => Number(row?.count || 0) > 0 && Number(key) > 1)
      .map(([key, row]) => ({
        key,
        label: t("passagensPage.mpInstallmentsShort", { count: Number(key), defaultValue: `${key}x` }),
        count: Number(row.count || 0),
        gmv: Number(row.gmv || 0),
      }))
      .sort((a, b) => Number(a.key) - Number(b.key));
  }, [vendasTotals, t]);

  const summaryPaymentBreakdown = useMemo(
    () =>
      breakdownEntries(totals.paymentBreakdown?.byMpPaymentType, (key) =>
        mpPaymentTypeLabel(key, undefined, t)
      ),
    [totals, t]
  );

  const summaryInstallmentsBreakdown = useMemo(() => {
    const byInst = totals.paymentBreakdown?.byInstallments || {};
    return Object.entries(byInst)
      .filter(([key, row]) => Number(row?.count || 0) > 0 && Number(key) > 1)
      .map(([key, row]) => ({
        key,
        label: t("passagensPage.mpInstallmentsShort", { count: Number(key), defaultValue: `${key}x` }),
        count: Number(row.count || 0),
        gmv: Number(row.gmv || 0),
      }))
      .sort((a, b) => Number(a.key) - Number(b.key));
  }, [totals, t]);

  const vendasPeriodLabel = useMemo(
    () => formatProductUsagePeriodBR(vendas?.period?.from || from, vendas?.period?.to || to),
    [vendas, from, to]
  );

  const vendasTableColumns = useMemo(() => {
    const cols = [];
    if (!isWorkspaceView && !dominioId) {
      cols.push({
        id: "workspace",
        label: t("platformPage.productUsageColWorkspace"),
        render: (row) => (
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={() => navigate(workspaceDetailPath(row.dominioId, "passenger-sales"))}
            sx={{ textAlign: "left" }}
          >
            {row.dominio}
          </Link>
        ),
      });
    }
    cols.push(
      {
        id: "date",
        label: t("passagensPage.colDate"),
        render: (row) => fmtDateTime(row.emitido_em || row.criado_em),
      },
      {
        id: "passenger",
        label: t("passagensPage.colClient"),
        render: (row) => row.nome_passageiro || "—",
      },
      {
        id: "route",
        label: t("passagensPage.colRoute"),
        render: (row) => [row.origem, row.destino].filter(Boolean).join(" → ") || "—",
      },
      {
        id: "value",
        label: t("passagensPage.colValue"),
        align: "right",
        render: (row) => <PassengerSaleValueCell row={row} />,
      },
      {
        id: "status",
        label: t("passagensPage.colStatus"),
        render: (row) => <Chip size="small" variant="outlined" label={statusLabel(row.status_venda) || "—"} />,
      },
      {
        id: "payment",
        label: t("passagensPage.colPayment"),
        render: (row) => <PassengerPaymentCell row={row} t={t} />,
      },
      {
        id: "partner",
        label: t("passagensPage.colPartner"),
        render: (row) => row.partner_key || "—",
      }
    );
    return cols;
  }, [dominioId, isWorkspaceView, navigate, statusLabel, t]);

  const parityRows = useMemo(
    () =>
      sortParityRows(
        (parityResult?.tenants || []).map((row) => ({
          ...row,
          _rowKey: String(row.dominioId),
        }))
      ),
    [parityResult, sortParityRows]
  );

  const filterPanel = (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 1,
        bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "grey.50"),
      }}
    >
      <Box sx={passengerSalesFilterGridSx}>
        <TextField
          label={t("platformPage.productUsageFrom")}
          type="date"
          size="small"
          fullWidth
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ lang: "pt-BR" }}
          sx={passengerSalesFilterFieldSx}
        />
        <TextField
          label={t("platformPage.productUsageTo")}
          type="date"
          size="small"
          fullWidth
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ lang: "pt-BR" }}
          sx={passengerSalesFilterFieldSx}
        />
        {!isWorkspaceView ? (
          <FormControl size="small" fullWidth sx={passengerSalesFilterFieldSx}>
            <InputLabel>{t("platformPage.productUsageFilterWorkspace")}</InputLabel>
            <Select
              label={t("platformPage.productUsageFilterWorkspace")}
              value={dominioId}
              onChange={(e) => setDominioId(e.target.value)}
            >
              <MenuItem value="">{t("platformPage.productUsageFilterAll")}</MenuItem>
              {workspaceOptions.map((w) => (
                <MenuItem key={w.id} value={String(w.id)}>
                  {w.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}
        {mainTab === 1 ? (
          <>
            <FormControl size="small" fullWidth sx={passengerSalesFilterFieldSx}>
              <InputLabel>{t("platformPage.passengerSalesFilterStatus")}</InputLabel>
              <Select
                label={t("platformPage.passengerSalesFilterStatus")}
                value={status}
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="">{t("platformPage.productUsageFilterAll")}</MenuItem>
                {STATUS_KEYS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabel(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              fullWidth
              label={t("platformPage.passengerSalesFilterPartner")}
              value={partnerKey}
              onChange={(e) => setPartnerKey(e.target.value)}
              sx={passengerSalesFilterFieldSx}
            />
            <TextField
              size="small"
              fullWidth
              label={t("platformPage.passengerSalesFilterSearch")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("platformPage.passengerSalesFilterSearchHint")}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                ...passengerSalesFilterFieldSx,
                gridColumn: { sm: "span 2", lg: "span 2" },
              }}
            />
          </>
        ) : null}
      </Box>
      <Stack
        direction="row"
        flexWrap="wrap"
        useFlexGap
        spacing={1}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ mt: 2, pt: 1.5, borderTop: 1, borderColor: "divider" }}
      >
        <BusyButton
          startIcon={<RefreshIcon />}
          variant="contained"
          size="small"
          onClick={load}
          loading={loading || vendasLoading}
          sx={{ flexShrink: 0, textTransform: "none", fontWeight: 600 }}
        >
          {t("billingPage.refresh")}
        </BusyButton>
      </Stack>
    </Paper>
  );

  return (
    <>
    <Box>
      {!isWorkspaceView ? (
        <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />} sx={{ mb: 1.5 }}>
          <Link
            component="button"
            variant="body2"
            underline={dominioId ? "hover" : "none"}
            color={dominioId ? "inherit" : "text.primary"}
            onClick={resetToPlatform}
            sx={{ cursor: "pointer", fontWeight: dominioId ? 400 : 600 }}
          >
            {t("platformPage.passengerSalesBreadcrumbPlatform")}
          </Link>
          {dominioId ? (
            <Typography variant="body2" color="text.primary" fontWeight={600}>
              {selectedWorkspaceLabel}
            </Typography>
          ) : null}
        </Breadcrumbs>
      ) : null}

      {!isWorkspaceView ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          {t("platformPage.passengerTicketSalesIntro")}
        </Typography>
      ) : null}

      {filterPanel}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}

      {Number(summary?.totals?.atencao_emissao || 0) > 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("platformPage.passengerAttentionEmissionAlert", {
            count: fmtCount(summary.totals.atencao_emissao),
          })}
        </Alert>
      ) : null}

      <Tabs
        value={mainTab}
        onChange={(_, v) => setMainTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("platformPage.passengerTabSummary")} />
        <Tab label={t("platformPage.passengerTabSales")} />
        <Tab label={t("platformPage.passengerTabOps")} />
      </Tabs>

      {mainTab === 0 ? (
        <>
          {loading && !summary ? (
            <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                {periodLabel ? (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, fontSize: "0.75rem" }}>
                    {periodLabel}
                  </Typography>
                ) : null}
                <AnalyticsKpiGrid>
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerTickets")}
                    value={fmtCount(totals.tickets)}
                    details={kpiMeta.tickets}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.tickets}
                    icon={ConfirmationNumberOutlinedIcon}
                    onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.tickets, t("platformPage.passengerTickets"))}
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerGmv")}
                    value={fmtMoney(totals.gmv)}
                    details={kpiMeta.gmv}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.gmv}
                    icon={AttachMoneyOutlinedIcon}
                    onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.gmv, t("platformPage.passengerGmv"))}
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerXpayTickets")}
                    value={fmtCount(totals.xpay_tickets)}
                    details={kpiMeta.xpayTickets}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.xpay}
                    icon={ConfirmationNumberOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.xpayTickets, t("platformPage.passengerXpayTickets"))
                    }
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerXpayGmv")}
                    value={fmtMoney(totals.xpay_gmv)}
                    details={kpiMeta.xpayGmv}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.xpay}
                    icon={AttachMoneyOutlinedIcon}
                    onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.xpayGmv, t("platformPage.passengerXpayGmv"))}
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerCancelled")}
                    value={fmtCount(totals.cancelados)}
                    details={kpiMeta.cancelados}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.cancelled}
                    icon={CancelOutlinedIcon}
                    onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.cancelados, t("platformPage.passengerCancelled"))}
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerChargebacks")}
                    value={fmtCount(totals.chargebacks)}
                    details={kpiMeta.chargebacks}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.chargebacks}
                    icon={ReportProblemOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.chargebacks, t("platformPage.passengerChargebacks"))
                    }
                  />
                </AnalyticsKpiGrid>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25, mb: 2, maxWidth: 720 }}>
                  {t("platformPage.passengerSalesOnlyCompletedHint")}
                </Typography>

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                  {t("platformPage.passengerFunnelSectionTitle")}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, maxWidth: 720 }}>
                  {t("platformPage.passengerFunnelSectionHint")}
                </Typography>
                <AnalyticsKpiGrid>
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFunnelInProgress")}
                    value={fmtCount(totals.em_andamento)}
                    details={kpiMeta.emAndamento}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.funnelOpen}
                    icon={TimelapseOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.emAndamento, t("platformPage.passengerFunnelInProgress"))
                    }
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFunnelLost")}
                    value={fmtCount(totals.vendas_perdidas)}
                    details={kpiMeta.vendasPerdidas}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.funnelLost}
                    icon={SentimentDissatisfiedOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.vendasPerdidas, t("platformPage.passengerFunnelLost"))
                    }
                  />
                </AnalyticsKpiGrid>

                <Box sx={{ mt: 3, mb: 1 }}>
                <AnalyticsKpiGrid>
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFeePartner")}
                    value={fmtMoney(feeTotals.taxa_parceiro)}
                    details={kpiMeta.taxaParceiro}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.feePartner}
                    icon={HandshakeOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.feePartner, t("platformPage.passengerFeePartner"))
                    }
                  />
                  <Tooltip title={t("platformPage.passengerFeeMpHint")}>
                    <Box sx={{ height: "100%" }}>
                      <AnalyticsKpiCard
                        title={t("platformPage.passengerFeeMp")}
                        value={fmtMoney(feeTotals.taxa_mp)}
                        details={kpiMeta.taxaMp}
                        periodLabel={periodLabel}
                        accentColor={KPI_ACCENT.feeMp}
                        icon={AccountBalanceOutlinedIcon}
                        onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.feeMp, t("platformPage.passengerFeeMp"))}
                      />
                    </Box>
                  </Tooltip>
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFeeXvision")}
                    value={fmtMoney(feeTotals.taxa_xvision)}
                    details={kpiMeta.taxaXvision}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.feeXvision}
                    icon={BusinessCenterOutlinedIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.feeXvision, t("platformPage.passengerFeeXvision"))
                    }
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFeeSurcharge")}
                    value={fmtMoney(feeTotals.sobretaxa)}
                    details={kpiMeta.sobretaxa}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.feeSurcharge}
                    icon={AddCircleOutlineIcon}
                    onClick={() =>
                      openKpiDrilldown(PASSENGER_KPI_METRICS.feeSurcharge, t("platformPage.passengerFeeSurcharge"))
                    }
                  />
                  <AnalyticsKpiCard
                    title={t("platformPage.passengerFeesTotal")}
                    value={fmtMoney(feeTotals.total)}
                    details={kpiMeta.feesTotal}
                    periodLabel={periodLabel}
                    accentColor={KPI_ACCENT.feesTotal}
                    icon={SummarizeOutlinedIcon}
                    onClick={() => openKpiDrilldown(PASSENGER_KPI_METRICS.feesTotal, t("platformPage.passengerFeesTotal"))}
                  />
                </AnalyticsKpiGrid>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.25, maxWidth: 720 }}>
                  {t("platformPage.passengerKpiDrilldownHint")} {t("platformPage.passengerFeesSummaryHint")}
                </Typography>
                </Box>
              </Box>

              {Number(paymentIntent.total_pending || 0) > 0 || Number(paymentIntent.undiagnosed || 0) > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
                    {t("platformPage.passengerPaymentIntentSectionTitle")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.25, maxWidth: 720 }}>
                    {t("platformPage.passengerPaymentIntentSectionHint")}
                  </Typography>
                  <AnalyticsKpiGrid>
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerPaymentIntentPending")}
                      value={fmtCount(paymentIntent.total_pending || 0)}
                      details={kpiMeta.paymentIntentPending}
                      periodLabel={periodLabel}
                      accentColor={KPI_ACCENT.paymentIntentPending}
                      icon={HourglassEmptyIcon}
                    />
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerPaymentIntentNotAccessed")}
                      value={fmtCount(paymentIntentByScenario.link_not_accessed || 0)}
                      details={kpiMeta.paymentIntentNotAccessed}
                      periodLabel={periodLabel}
                      accentColor={KPI_ACCENT.paymentIntentNotAccessed}
                      icon={LinkOffOutlinedIcon}
                    />
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerPaymentIntentRejected")}
                      value={fmtCount(paymentIntentByScenario.payment_rejected || 0)}
                      details={kpiMeta.paymentIntentRejected}
                      periodLabel={periodLabel}
                      accentColor={KPI_ACCENT.paymentIntentRejected}
                      icon={BlockOutlinedIcon}
                    />
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerPaymentIntentExpired")}
                      value={fmtCount(paymentIntentByScenario.link_expired || 0)}
                      details={kpiMeta.paymentIntentExpired}
                      periodLabel={periodLabel}
                      accentColor={KPI_ACCENT.paymentIntentExpired}
                      icon={ReportProblemOutlinedIcon}
                    />
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerPaymentIntentInReview")}
                      value={fmtCount(paymentIntentByScenario.payment_pending || 0)}
                      details={kpiMeta.paymentIntentInReview}
                      periodLabel={periodLabel}
                      accentColor={KPI_ACCENT.mpPending}
                      icon={HourglassEmptyIcon}
                    />
                  </AnalyticsKpiGrid>
                </Box>
              ) : null}

              {summaryPaymentBreakdown.length > 0 ? (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                    {t("platformPage.passengerSalesByPaymentMethod")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {t("platformPage.passengerKpiDrilldownHint")}
                  </Typography>
                  <AnalyticsKpiGrid>
                    {summaryPaymentBreakdown.slice(0, 6).map((item) => (
                      <AnalyticsKpiCard
                        key={item.key}
                        title={item.label}
                        value={fmtCount(item.count)}
                        details={kpiLines(
                          `${fmtMoney(item.gmv)} GMV`,
                          fmtPct(item.count, totals.tickets)
                            ? t("platformPage.passengerKpiPctOfTickets", {
                                pct: fmtPct(item.count, totals.tickets),
                              })
                            : null,
                          fmtPct(item.gmv, totals.gmv)
                            ? t("platformPage.passengerKpiPctOfGmv", {
                                pct: fmtPct(item.gmv, totals.gmv),
                              })
                            : null,
                          item.key === "mp_pending" ? t("platformPage.passengerMpPendingHint") : null
                        )}
                        periodLabel={periodLabel}
                        accentColor={
                          item.key === "pix"
                            ? KPI_ACCENT.paymentPix
                            : item.key === "credit_card"
                              ? KPI_ACCENT.paymentCard
                              : item.key === "account_money"
                                ? KPI_ACCENT.paymentMpBalance
                                : item.key === "mp_pending"
                                  ? KPI_ACCENT.mpPending
                                  : KPI_ACCENT.feesTotal
                        }
                        icon={item.key === "pix" ? PixOutlinedIcon : CreditCardOutlinedIcon}
                        onClick={() => openPaymentTypeDrilldown(item)}
                        action={
                          item.key === "mp_pending" ? (
                            <Tooltip title={t("platformPage.passengerMpPendingSyncTooltip")}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={mpPendingSyncing}
                                  onClick={runMpPendingSync}
                                  aria-label={t("platformPage.passengerMpPendingSync")}
                                  sx={{ mt: -0.25 }}
                                >
                                  {mpPendingSyncing ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <SyncIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null
                        }
                      />
                    ))}
                  </AnalyticsKpiGrid>
                  {mpPendingSyncMsg ? (
                    <Typography
                      variant="caption"
                      color={mpPendingSyncIsError ? "error" : "text.secondary"}
                      display="block"
                      sx={{ mt: 1 }}
                    >
                      {mpPendingSyncMsg}
                    </Typography>
                  ) : null}
                  {summaryInstallmentsBreakdown.length > 0 ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {t("platformPage.passengerSalesByInstallments")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {summaryInstallmentsBreakdown.map((item) => (
                          <Chip
                            key={item.key}
                            variant="outlined"
                            label={`${item.label}: ${fmtCount(item.count)} · ${fmtMoney(item.gmv)}`}
                            onClick={() => openInstallmentsDrilldown(item)}
                            sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Box>
              ) : null}

              {!isWorkspaceView ? (
              <AnalyticsSection
                title={dominioId ? t("platformPage.passengerWorkspaceDetailTitle", { name: selectedWorkspaceLabel }) : t("platformPage.passengerHierarchyTitle")}
                hint={t("platformPage.passengerHierarchyHint")}
                loading={loading}
              >
                <AnalyticsDataTable
                  rows={tenantRows}
                  emptyLabel={t("platformPage.passengerSalesEmpty")}
                  sortBy={tenantSortBy}
                  sortOrder={tenantSortOrder}
                  onSort={handleTenantSort}
                  sortFields={TENANT_SORT_FIELDS}
                  onRowClick={
                    dominioId
                      ? undefined
                      : (row) => setDominioId(String(row.dominioId))
                  }
                  columns={[
                    {
                      id: "workspace",
                      label: t("platformPage.productUsageColWorkspace"),
                      render: (row) => row.dominio,
                    },
                    {
                      id: "tickets",
                      label: t("platformPage.passengerTickets"),
                      align: "right",
                      render: (row) => fmtCount(row.tickets),
                    },
                    {
                      id: "gmv",
                      label: t("platformPage.passengerGmv"),
                      align: "right",
                      render: (row) => fmtMoney(row.gmv),
                    },
                    {
                      id: "cancelled",
                      label: t("platformPage.passengerCancelled"),
                      align: "right",
                      render: (row) => fmtCount(row.cancelados),
                    },
                    {
                      id: "actions",
                      label: "",
                      align: "right",
                      render: (row) => (
                        <Button
                          component={RouterLink}
                          to={workspaceDetailPath(row.dominioId, "passenger-sales")}
                          size="small"
                          endIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t("platformPage.passengerOpenWorkspace")}
                        </Button>
                      ),
                    },
                  ]}
                />
              </AnalyticsSection>
              ) : null}
            </>
          )}
        </>
      ) : null}

      {mainTab === 1 ? (
        <AnalyticsSection
          title={t("platformPage.passengerTabSales")}
          hint={t("platformPage.passengerSalesListHint")}
          loading={vendasLoading}
        >
          {vendasLoading && !vendas ? (
            <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              {vendasTotals ? (
                <Box sx={{ mb: 2.5 }}>
                  <AnalyticsKpiGrid>
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerSalesTotalsCount")}
                      value={fmtCount(
                        status
                          ? vendasTotals.count
                          : Number(vendasTotals.byStatus?.emitida?.count ?? vendasTotals.count)
                      )}
                      periodLabel={vendasPeriodLabel}
                      accentColor={KPI_ACCENT.tickets}
                      icon={ConfirmationNumberOutlinedIcon}
                    />
                    <AnalyticsKpiCard
                      title={t("platformPage.passengerSalesTotalsGmv")}
                      value={fmtMoney(
                        status
                          ? vendasTotals.gmv
                          : Number(vendasTotals.byStatus?.emitida?.gmv ?? vendasTotals.gmv)
                      )}
                      periodLabel={vendasPeriodLabel}
                      accentColor={KPI_ACCENT.gmv}
                      icon={AttachMoneyOutlinedIcon}
                    />
                  </AnalyticsKpiGrid>
                  {vendasStatusBreakdown.length > 0 ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {t("platformPage.passengerSalesByStatus")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {vendasStatusBreakdown.map((item) => (
                          <Chip
                            key={item.key}
                            variant="outlined"
                            label={`${statusLabel(item.key)}: ${fmtCount(item.count)} · ${fmtMoney(item.gmv)}`}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                  {vendasPaymentBreakdown.length > 0 ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {t("platformPage.passengerSalesByPaymentMethod")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {vendasPaymentBreakdown.map((item) => (
                          <Chip
                            key={item.key}
                            variant="outlined"
                            label={`${item.label}: ${fmtCount(item.count)} · ${fmtMoney(item.gmv)}`}
                            onClick={() => openPaymentTypeDrilldown(item)}
                            sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                  {vendasInstallmentsBreakdown.length > 0 ? (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {t("platformPage.passengerSalesByInstallments")}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {vendasInstallmentsBreakdown.map((item) => (
                          <Chip
                            key={item.key}
                            variant="outlined"
                            label={`${item.label}: ${fmtCount(item.count)} · ${fmtMoney(item.gmv)}`}
                            onClick={() => openInstallmentsDrilldown(item)}
                            sx={{ cursor: "pointer", "&:hover": { bgcolor: "action.hover" } }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Box>
              ) : null}
              <AnalyticsDataTable
                rows={vendasRows}
                emptyLabel={t("platformPage.passengerSalesListEmpty")}
                sortBy={vendasSortBy}
                sortOrder={vendasSortOrder}
                onSort={handleVendasSortChange}
                sortFields={VENDAS_SORT_FIELDS}
                columns={vendasTableColumns}
              />
              {vendas?.total > 0 ? (
                <TablePagination
                  component="div"
                  count={Number(vendas.total) || 0}
                  page={vendasPage}
                  onPageChange={(_, nextPage) => setVendasPage(nextPage)}
                  rowsPerPage={vendasRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setVendasRowsPerPage(Number.parseInt(e.target.value, 10));
                    setVendasPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage={t("platformPage.passengerSalesRowsPerPage")}
                  labelDisplayedRows={({ from, to, count }) =>
                    t("platformPage.passengerSalesListFooter", { shown: `${from}-${to}`, total: count })
                  }
                  sx={{ mt: 0.5, borderTop: 1, borderColor: "divider" }}
                />
              ) : null}
            </>
          )}
        </AnalyticsSection>
      ) : null}

      {mainTab === 2 ? (
        <Stack spacing={0}>
          {FEATURE_PASSAGENS_REPASSES_ENABLED ? (
            <PassengerRepasseRequestsPanel dominioIdFilter={dominioId || undefined} />
          ) : null}

          {dominioId ? (
            <AnalyticsSection
              title={t("platformPage.passengerFinancialExport")}
              hint={t("platformPage.passengerWorkspaceExportsHint", {
                defaultValue: "Extratos e relatórios Mercado Pago deste workspace no período filtrado.",
              })}
              sx={{ mt: 2 }}
            >
              <PassengerWorkspaceFinancialExports dominioId={dominioId} from={from} to={to} />
            </AnalyticsSection>
          ) : null}

          <PassengerMercadoPagoAuditPanel
            dominioId={dominioId || undefined}
            from={from}
            to={to}
            salesTotals={totals}
            salesPeriod={summary?.period}
            onSyncComplete={async () => {
              try {
                let offsets = {};
                let tenantOffset = 0;
                for (let i = 0; i < 40; i += 1) {
                  const data = await postPlatformPassengerFeesBackfill({
                    from,
                    to,
                    dominioId: dominioId || undefined,
                    offsets,
                    tenantOffset,
                    forceMpBackfill: true,
                  });
                  offsets = data?.offsets && typeof data.offsets === "object" ? data.offsets : {};
                  tenantOffset = Number(data?.tenantOffset ?? tenantOffset);
                  if (!data?.hasMore) break;
                }
              } catch {
                /* best effort */
              }
              await loadSummary();
            }}
          />

          <PassengerPartnerReportAuditPanel dominioId={dominioId || undefined} from={from} to={to} />

          <AnalyticsSection
            title={t("platformPage.passengerOpsTabTaxes", { defaultValue: "Taxas" })}
            hint={t("platformPage.passengerTaxProfilesHint")}
            sx={{ mt: 2 }}
          >
            <Stack spacing={2}>
              <PassengerFeesRecalcButton
                from={from}
                to={to}
                dominioId={dominioId || undefined}
                onComplete={loadSummary}
              />
              <PassengerPartnerGlobalFeesPanel
                from={from}
                to={to}
                dominioId={dominioId || undefined}
                onProfilesSaved={loadSummary}
              />
              <PassengerTaxProfilesPanel
                dominioId={dominioId || undefined}
                from={from}
                to={to}
                onProfilesSaved={loadSummary}
              />
            </Stack>
          </AnalyticsSection>

          <AnalyticsSection
            title={t("platformPage.passengerMigrationTitle")}
            hint={t("platformPage.passengerMigrationIntro")}
          >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 2 }}>
              <Tooltip title={t("platformPage.passengerParityCheckAllTooltip")}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={parityLoading ? <CircularProgress size={16} /> : <FactCheckIcon />}
                    onClick={runParityCheck}
                    disabled={parityLoading || backfillRunning}
                  >
                    {t("platformPage.passengerParityCheckAll")}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={t("platformPage.passengerBackfillAllTooltip")}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    color="warning"
                    startIcon={backfillRunning ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                    onClick={runBackfillAll}
                    disabled={backfillRunning || parityLoading}
                  >
                    {t("platformPage.passengerBackfillAll")}
                  </Button>
                </span>
              </Tooltip>
            </Stack>

            {backfillRunning && backfillProgress?.items?.length ? (
              <Box sx={{ mb: 2 }}>
                {backfillProgressSummary ? (
                  <Box sx={{ mb: 1.5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t("platformPage.passengerBackfillProgressSummary", {
                          done: backfillProgressSummary.done,
                          total: backfillProgressSummary.total,
                          ok: backfillProgressSummary.ok,
                          skipped: backfillProgressSummary.skipped,
                          failed: backfillProgressSummary.failed,
                          synced: backfillProgress.totalSynced,
                        })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {Math.round((backfillProgressSummary.done / backfillProgressSummary.total) * 100)}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={(backfillProgressSummary.done / backfillProgressSummary.total) * 100}
                      sx={{ mb: 1 }}
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      {t("platformPage.passengerBackfillProgress", {
                        batch: backfillProgress.batch,
                        synced: backfillProgress.totalSynced,
                        errors: backfillProgress.totalErrors,
                        tenants: backfillProgressSummary.total,
                      })}
                    </Typography>
                  </Box>
                ) : (
                  <LinearProgress sx={{ mb: 1 }} />
                )}
                {renderBackfillItemsList(backfillProgress.items, "progress")}
              </Box>
            ) : null}

            {backfillResults && !backfillRunning ? (
              <Box sx={{ mb: 2 }}>
                {backfillResults.error ? (
                  <Alert severity={backfillResults.partial ? "warning" : "error"} sx={{ mb: 1.5 }}>
                    {backfillResults.error}
                  </Alert>
                ) : null}
                {!backfillResults.error || backfillResults.ok > 0 ? (
                  <Alert
                    severity={
                      backfillResults.partial || backfillResults.failed > 0 || backfillResults.skipped > 0
                        ? "warning"
                        : "success"
                    }
                    sx={{ mb: 1.5 }}
                  >
                    {t("platformPage.passengerBackfillDoneSummary", {
                      synced: backfillResults.synced ?? 0,
                      total: backfillResults.total ?? 0,
                      ok: backfillResults.ok ?? 0,
                      skipped: backfillResults.skipped ?? 0,
                      failed: backfillResults.failed ?? 0,
                      autoMigrated: backfillResults.autoMigrated ?? 0,
                      duration: ((backfillResults.durationMs || 0) / 1000).toFixed(1),
                    })}
                  </Alert>
                ) : null}
                {backfillResults.items?.length ? renderBackfillItemsList(backfillResults.items, "result") : null}
                <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
                  <Button size="small" onClick={() => setBackfillResults(null)}>
                    {t("platformPage.passengerBackfillDismiss")}
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {parityError ? <Alert severity="error" sx={{ mb: 1.5 }}>{parityError}</Alert> : null}

            {parityResult?.summary ? (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" variant="outlined" label={`${t("platformPage.passengerParityTotal")}: ${parityResult.summary.total}`} />
                <Chip size="small" color="success" variant="outlined" label={`${t("platformPage.passengerParityOkLabel")}: ${parityResult.summary.ok}`} />
                <Chip size="small" color="warning" variant="outlined" label={`${t("platformPage.passengerParityWarning")}: ${parityResult.summary.warning}`} />
                <Chip size="small" color="error" variant="outlined" label={`${t("platformPage.passengerParityErrorLabel")}: ${parityResult.summary.error}`} />
                <Chip size="small" variant="outlined" label={`${t("platformPage.passengerParityPending")}: ${parityResult.summary.migration_pending}`} />
              </Stack>
            ) : null}

            {parityLoading ? <LinearProgress sx={{ mb: 1.5 }} /> : null}

            <AnalyticsDataTable
              rows={parityRows}
              emptyLabel={t("platformPage.passengerParityEmpty")}
              sortBy={paritySortBy}
              sortOrder={paritySortOrder}
              onSort={handleParitySort}
              sortFields={PARITY_SORT_FIELDS}
              columns={[
                { id: "workspace", label: t("platformPage.productUsageColWorkspace"), render: (row) => row.dominio },
                {
                  id: "legacy",
                  label: t("platformPage.passengerParityColLegacy"),
                  align: "right",
                  render: (row) => row.legacy_bpe_messages ?? "—",
                },
                {
                  id: "index",
                  label: t("platformPage.passengerParityColIndex"),
                  align: "right",
                  render: (row) => row.passagens_vendas_rows ?? "—",
                },
                {
                  id: "delta",
                  label: t("platformPage.passengerParityColDelta"),
                  align: "right",
                  render: (row) => row.delta ?? "—",
                },
                {
                  id: "status",
                  label: t("passagensPage.colStatus"),
                  render: (row) => (
                    <Chip size="small" color={PARITY_STATUS_COLOR[row.status] || "default"} label={row.status || "—"} />
                  ),
                },
              ]}
            />
          </AnalyticsSection>
        </Stack>
      ) : null}
    </Box>

    {kpiDrilldown ? (
      <PassengerKpiDrilldownDrawer
        key={kpiDrilldown.key}
        open
        onClose={() => setKpiDrilldown(null)}
        kpiKey={kpiDrilldown.kpi}
        title={kpiDrilldown.title}
        from={from}
        to={to}
        dominioId={dominioId || undefined}
        showWorkspaceColumn={!isWorkspaceView && !dominioId}
        statusLabel={statusLabel}
        mpPaymentType={kpiDrilldown.mpPaymentType}
        mpInstallments={kpiDrilldown.mpInstallments}
      />
    ) : null}
    </>
  );
}
